import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

// Layouts
import MainLayout from './components/layouts/MainLayout';
import AuthLayout from './components/layouts/AuthLayout';

// Pages
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import TicketList from './pages/tickets/TicketList';
import TicketCreate from './pages/tickets/TicketCreate';
import TicketDetail from './pages/tickets/TicketDetail';
import FAQList from './pages/faq/FAQList';
import FAQDetail from './pages/faq/FAQDetail';
import FAQManage from './pages/faq/FAQManage';
import UserList from './pages/users/UserList';
import UserDetail from './pages/users/UserDetail';
import HotelList from './pages/hotels/HotelList';
import TeamList from './pages/teams/TeamList';
import Profile from './pages/Profile';
import NotFound from './pages/NotFound';

// Protected Route Component
const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner h-8 w-8"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner h-8 w-8"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <AuthLayout>
              <Login />
            </AuthLayout>
          </PublicRoute>
        }
      />

      {/* SSO Callback */}
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />

        {/* Tickets */}
        <Route path="tickets" element={<TicketList />} />
        <Route path="tickets/new" element={<TicketCreate />} />
        <Route path="tickets/:id" element={<TicketDetail />} />

        {/* FAQ */}
        <Route path="faq" element={<FAQList />} />
        <Route path="faq/:id" element={<FAQDetail />} />
        <Route
          path="faq/manage"
          element={
            <ProtectedRoute roles={['Super Admin', 'Admin', 'Content Manager']}>
              <FAQManage />
            </ProtectedRoute>
          }
        />

        {/* Users (Admin only) */}
        <Route
          path="users"
          element={
            <ProtectedRoute roles={['Super Admin', 'Admin', 'Team Lead']}>
              <UserList />
            </ProtectedRoute>
          }
        />
        <Route
          path="users/:id"
          element={
            <ProtectedRoute roles={['Super Admin', 'Admin', 'Team Lead']}>
              <UserDetail />
            </ProtectedRoute>
          }
        />

        {/* Hotels (Admin only) */}
        <Route
          path="hotels"
          element={
            <ProtectedRoute roles={['Super Admin', 'Admin']}>
              <HotelList />
            </ProtectedRoute>
          }
        />

        {/* Teams (Admin only) */}
        <Route
          path="teams"
          element={
            <ProtectedRoute roles={['Super Admin', 'Admin']}>
              <TeamList />
            </ProtectedRoute>
          }
        />

        {/* Profile */}
        <Route path="profile" element={<Profile />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default App;
